import React, { useContext, useEffect, useMemo, useState } from 'react';
import { GlobalStateContext } from '../../components/general/GlobalStateProvider';
import { FlatList, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import MaterialCommunityIcons from "@expo/vector-icons/MaterialCommunityIcons";
import MessagingService, { Chat, Message } from '../../data/MessagingService';

const parseTime = (input: any): Date | null => {
    try {
        if (!input) return null;
        if (input?.toDate) return input.toDate();
        const parsed = new Date(input);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    } catch {
        return null;
    }
};

const formatTimeLabel = (input: any) => {
    const date = parseTime(input);
    if (!date) return '';
    const now = new Date();
    if (date.toDateString() === now.toDateString()) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const getChatTitle = (chat: Chat, currentUserId: string) => {
    const idx = Array.isArray(chat.participantIds) ? chat.participantIds.findIndex((id) => id !== currentUserId) : -1;
    if (idx >= 0 && Array.isArray(chat.participantNames) && chat.participantNames[idx]) return String(chat.participantNames[idx]);
    if (idx >= 0) return String(chat.participantIds[idx]);
    return chat.name || 'Chat';
};

const MessagesPage = () => {
    const { padding, user } = useContext(GlobalStateContext);
    const userId = user?.userId || '';
    const [chats, setChats] = useState<Chat[]>([]);
    const [selectedChatId, setSelectedChatId] = useState<string>('');
    const [messages, setMessages] = useState<Message[]>([]);
    const [composer, setComposer] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        if (!userId) return;
        const unsub = MessagingService.listenToUserChats(userId, (list) => setChats(list || []));
        return () => unsub && unsub();
    }, [userId]);

    useEffect(() => {
        if (!selectedChatId && chats.length > 0) setSelectedChatId(chats[0].chatId);
    }, [chats, selectedChatId]);

    useEffect(() => {
        if (!selectedChatId || !userId) {
            setMessages([]);
            return;
        }
        const unsub = MessagingService.listenToChatMessages(selectedChatId, (list) => setMessages(list || []), 100);
        MessagingService.markChatRead(selectedChatId, userId).catch(() => null);
        return () => unsub && unsub();
    }, [selectedChatId, userId]);

    const selectedChat = useMemo(() => chats.find((chat) => chat.chatId === selectedChatId) || null, [chats, selectedChatId]);

    const filteredChats = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return chats;
        return chats.filter((chat) => {
            const title = getChatTitle(chat, userId).toLowerCase();
            const preview = String(chat.lastMessage || '').toLowerCase();
            return title.includes(q) || preview.includes(q);
        });
    }, [chats, searchQuery, userId]);

    const sendMessage = async () => {
        const text = composer.trim();
        if (!text || !selectedChatId || !userId || isSending) return;
        try {
            setIsSending(true);
            await MessagingService.sendMessage(selectedChatId, userId, { text });
            setComposer('');
            await MessagingService.markChatRead(selectedChatId, userId);
        } finally {
            setIsSending(false);
        }
    };

    if (!userId) {
        return (
            <View style={[styles.emptyRoot, { paddingHorizontal: padding }]}>
                <MaterialCommunityIcons name="message-lock-outline" size={48} color="#9CA3AF" />
                <Text style={styles.emptyTitle}>Sign in to use messages</Text>
                <Text style={styles.emptyDescription}>Chat with families, hosts, and coaches once you are signed in.</Text>
            </View>
        );
    }

    return (
        <View style={[styles.container, { paddingHorizontal: padding }]}>
            <View style={styles.titleContainer}>
                <Text style={styles.titleText}>Messages</Text>
                <Text style={styles.subtitleText}>Realtime chat synced with SnapCamp mobile.</Text>
            </View>

            <View style={styles.layoutRow}>
                <View style={styles.leftColumn}>
                    <View style={styles.searchBox}>
                        <MaterialCommunityIcons name="comment-search-outline" size={20} color="#6B7280" />
                        <TextInput
                            style={styles.searchInput}
                            placeholder="Search conversations..."
                            value={searchQuery}
                            onChangeText={setSearchQuery}
                        />
                    </View>
                    <FlatList
                        data={filteredChats}
                        keyExtractor={(item) => item.chatId}
                        contentContainerStyle={styles.previewList}
                        ListEmptyComponent={<View style={styles.inlineEmpty}><Text style={styles.inlineEmptyText}>No chats yet.</Text></View>}
                        renderItem={({ item }) => {
                            const active = item.chatId === selectedChatId;
                            const unreadCount = (item.unreadCountByUser && (item.unreadCountByUser as any)[userId]) || 0;
                            return (
                                <Pressable style={[styles.messagePreviewContainer, active && styles.activeMessage]} onPress={() => setSelectedChatId(item.chatId)}>
                                    <View style={styles.previewTopRow}>
                                        <Text style={styles.messageName} numberOfLines={1}>{getChatTitle(item, userId)}</Text>
                                        <Text style={styles.timestamp}>{formatTimeLabel(item.lastMessageTimestamp)}</Text>
                                    </View>
                                    <View style={styles.previewBottomRow}>
                                        <Text style={styles.messagePreview} numberOfLines={1}>{item.lastMessage || 'Start a conversation'}</Text>
                                        {unreadCount > 0 && (
                                            <View style={styles.unreadBadge}>
                                                <Text style={styles.unreadText}>{unreadCount > 99 ? '99+' : unreadCount}</Text>
                                            </View>
                                        )}
                                    </View>
                                </Pressable>
                            );
                        }}
                    />
                </View>

                <View style={styles.rightColumn}>
                    {selectedChat ? (
                        <>
                            <View style={styles.chatHeader}>
                                <Text style={styles.chatHeaderTitle}>{getChatTitle(selectedChat, userId)}</Text>
                                <Text style={styles.chatHeaderSub}>Live conversation</Text>
                            </View>
                            <ScrollView contentContainerStyle={styles.fullMessageContainer}>
                                {messages.map((msg) => {
                                    const isMine = msg.senderId === userId;
                                    return (
                                        <View key={msg.messageId} style={[styles.bubbleRow, isMine ? styles.mineRow : styles.theirRow]}>
                                            <View style={[styles.bubble, isMine ? styles.mineBubble : styles.theirBubble]}>
                                                <Text style={[styles.bubbleText, isMine && styles.mineBubbleText]}>{msg.text || '[Attachment]'}</Text>
                                                <Text style={[styles.bubbleTime, isMine && styles.mineBubbleTime]}>{formatTimeLabel(msg.createdAt)}</Text>
                                            </View>
                                        </View>
                                    );
                                })}
                            </ScrollView>
                            <View style={styles.replyBox}>
                                <TextInput style={styles.replyInput} placeholder="Type your message..." value={composer} onChangeText={setComposer} />
                                <Pressable style={[styles.sendButton, isSending && styles.sendButtonDisabled]} onPress={sendMessage}>
                                    <Text style={styles.sendButtonText}>{isSending ? 'Sending...' : 'Send'}</Text>
                                </Pressable>
                            </View>
                        </>
                    ) : (
                        <View style={styles.rightEmpty}>
                            <MaterialCommunityIcons name="chat-outline" size={42} color="#9CA3AF" />
                            <Text style={styles.emptyTitle}>Select a conversation</Text>
                            <Text style={styles.emptyDescription}>Choose a chat from the left to view and reply.</Text>
                        </View>
                    )}
                </View>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: { flex: 1, flexDirection: 'column' },
    titleContainer: { flexDirection: 'column', paddingHorizontal: 12, paddingTop: 14, marginBottom: 18 },
    titleText: { fontSize: 34, fontWeight: 'bold', marginBottom: 6 },
    subtitleText: { fontSize: 14, color: '#6B7280' },
    layoutRow: { flexDirection: 'row', gap: 16 },
    searchBox: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F9F9F9',
        borderRadius: 12,
        paddingHorizontal: 12,
        margin: 10,
        borderWidth: 1,
        borderColor: '#E5E7EB',
    },
    searchInput: { flex: 1, height: 42, fontSize: 14, paddingLeft: 8 },
    leftColumn: {
        width: '32%',
        backgroundColor: '#FFFFFF',
        borderRightWidth: 1,
        borderRightColor: '#E0E0E0',
        height: 640,
        borderRadius: 14,
        overflow: 'hidden',
    },
    previewList: { padding: 10 },
    messagePreviewContainer: {
        padding: 12,
        borderWidth: 1,
        borderColor: '#F0F0F0',
        borderRadius: 12,
        marginBottom: 8,
        backgroundColor: '#FFFFFF',
    },
    activeMessage: { backgroundColor: '#F8FAFF', borderColor: '#BFDBFE' },
    previewTopRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
    previewBottomRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 4, gap: 8 },
    messageName: { flex: 1, fontSize: 15, fontWeight: '700' },
    messagePreview: { flex: 1, fontSize: 14, color: '#666' },
    timestamp: { fontSize: 12, color: '#999' },
    rightColumn: {
        width: '66%',
        height: 640,
        backgroundColor: '#FAFAFA',
        borderRadius: 14,
        overflow: 'hidden',
    },
    chatHeader: {
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: '#E5E7EB',
        backgroundColor: '#FFFFFF',
    },
    chatHeaderTitle: { fontSize: 17, fontWeight: '700', color: '#111827' },
    chatHeaderSub: { fontSize: 12, color: '#6B7280', marginTop: 2 },
    fullMessageContainer: { padding: 16, gap: 8, flexGrow: 1 },
    bubbleRow: { width: '100%', flexDirection: 'row' },
    mineRow: { justifyContent: 'flex-end' },
    theirRow: { justifyContent: 'flex-start' },
    bubble: { maxWidth: '76%', borderRadius: 16, paddingHorizontal: 12, paddingVertical: 9 },
    mineBubble: { backgroundColor: '#0EA5A4' },
    theirBubble: { backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: '#E5E7EB' },
    bubbleText: { fontSize: 14, lineHeight: 20, color: '#111827' },
    mineBubbleText: { color: '#ECFEFF' },
    bubbleTime: { marginTop: 4, fontSize: 11, color: '#6B7280', textAlign: 'right' },
    mineBubbleTime: { color: '#CCFBF1' },
    replyBox: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingHorizontal: 14,
        paddingVertical: 12,
        borderTopWidth: 1,
        borderTopColor: '#E5E7EB',
        backgroundColor: '#FFFFFF',
    },
    replyInput: {
        flex: 1,
        height: 44,
        borderColor: '#D1D5DB',
        borderWidth: 1,
        borderRadius: 999,
        paddingHorizontal: 16,
        fontSize: 14,
        backgroundColor: '#F9FAFB',
    },
    sendButton: { backgroundColor: '#111827', borderRadius: 999, paddingHorizontal: 16, paddingVertical: 10 },
    sendButtonText: { color: 'white', fontSize: 14, fontWeight: '600' },
    sendButtonDisabled: { opacity: 0.65 },
    unreadBadge: {
        minWidth: 18,
        height: 18,
        borderRadius: 9,
        backgroundColor: '#EF4444',
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 5,
    },
    unreadText: { color: '#fff', fontSize: 10, fontWeight: '700' },
    rightEmpty: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 24,
        gap: 10,
    },
    emptyRoot: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 10 },
    emptyTitle: { fontSize: 22, fontWeight: '700', color: '#111827' },
    emptyDescription: { fontSize: 14, color: '#6B7280', textAlign: 'center', maxWidth: 460, lineHeight: 22 },
    inlineEmpty: { padding: 24, alignItems: 'center' },
    inlineEmptyText: { color: '#6B7280' },
});

export default MessagesPage;

